import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SellertoolSignatureUtils } from "../../../../../utils/sellertool-signature-utils";
import { useApiInfoContextValueHook } from "../../../../../contexts/ApiInfoContext";
import { CustomDateUtils } from "../../../../../utils/CustomDateUtils";
import { ShoppingCartStageApi } from "../../../../../apis/ShoppingCartStageApi";
import { CustomBackdropController } from "../../../../../components/backdrop/default/v1";
import { useEffect } from "react";
import { customToast } from "../../../../../components/toast/custom-react-toastify/v1";

const QUERY_KEY = 'shoppingCartStages';

export function useShoppingCartStagesQuery({
    dateRange,
}) {
    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const queryResult = useQuery({
        queryKey: [QUERY_KEY, dateRange],
        enabled: !!(apiInfoContextValueHook?.apiInfo?.apiKey && apiInfoContextValueHook?.apiInfo?.secretKey && dateRange?.startDate && dateRange?.endDate),
        queryFn: async () => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }
            const fetchResult = await ShoppingCartStageApi().searchList({
                params: {
                    startDateTime: CustomDateUtils().getStartDate(new Date(dateRange?.startDate)).toISOString(),
                    endDateTime: CustomDateUtils().getEndDate(new Date(dateRange?.endDate)).toISOString()
                },
                headers: headers
            })

            return fetchResult;
        }
    });

    useEffect(() => {
        if (queryResult.isLoading) {
            CustomBackdropController().showBackdrop();
        } else {
            CustomBackdropController().hideBackdrop();
        }
    }, [queryResult.isLoading]);

    return queryResult;
}

export function useCreateShoppingCartStagesMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartStageApi().createList({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('장바구니 스테이지가 추가되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useUpdateShoppingCartStagesMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartStageApi().updateOne({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('장바구니 스테이지가 수정되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useDeleteShoppingCartStagesMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ uuid }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartStageApi().deleteOne({
                headers: headers,
                params: {
                    uuid: uuid
                }
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('장바구니 스테이지가 삭제되었습니다.');
            queryClient.invalidateQueries({ queryKey: ["shoppingCartStages"] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}